import { ComponentType, ReactNode, useContext } from "react";
import { ConstructorTabContext } from "pages/CompetencyMaps/view/context";
import SelectionCheckbox from "pages/CompetencyMaps/view/components/Selection/SelectionCheckbox";
import { SelectionEntity, SelectionScope } from "pages/CompetencyMaps/view/components/Selection/types";
import { getSelectionCheckboxState } from "pages/CompetencyMaps/view/components/Selection/utils";

export type WithSelectionCheckboxInjectedProps = {
  selectionMode: boolean;
  selectionCheckboxNode: ReactNode;
};

type WithSelectionCheckboxProps<T> = Omit<T, keyof WithSelectionCheckboxInjectedProps> & {
  selectionEntity?: SelectionEntity;
  selectionScope?: SelectionScope;
};

const withSelectionCheckbox = <T,>(Component: ComponentType<T>) => {
  // eslint-disable-next-line react/function-component-definition, react/display-name
  return (props: WithSelectionCheckboxProps<T>) => {
    const { selectionEntity, selectionScope = "constructor", ...rest } = props;
    const {
      selectionMode,
      selectionScope: activeSelectionScope,
      selectedEntities,
      selectionTree,
      onToggleSelectionEntity,
    } = useContext(ConstructorTabContext);
    const isSelectionActive = selectionMode && activeSelectionScope === selectionScope;
    const checkboxState =
      selectionEntity && isSelectionActive
        ? getSelectionCheckboxState(selectionEntity, selectedEntities, selectionTree)
        : { checked: false, indeterminate: false };

    return (
      <Component
        {...(rest as T)}
        selectionMode={isSelectionActive}
        selectionCheckboxNode={
          isSelectionActive && selectionEntity ? (
            <SelectionCheckbox
              checked={checkboxState.checked}
              indeterminate={checkboxState.indeterminate}
              onChange={() => onToggleSelectionEntity(selectionEntity)}
            />
          ) : null
        }
      />
    );
  };
};

export default withSelectionCheckbox;
